import { formatQuestionnaireAnswer } from '@liza/fhir-utils';
import type { Questionnaire, QuestionnaireItem, QuestionnaireResponse } from '@medplum/fhirtypes';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type QuestionnaireResponseAnswersProps = {
  questionnaireResponse: QuestionnaireResponse;
  /** Source Questionnaire, used to resolve question text — QR items carry only linkIds. */
  questionnaire: Questionnaire;
};

const collectItemText = (items: QuestionnaireItem[] | undefined, byLinkId: Map<string, string>): void => {
  for (const item of items ?? []) {
    if (item.linkId && item.text) byLinkId.set(item.linkId, item.text);
    collectItemText(item.item, byLinkId);
  }
};

/**
 * Read-only question/answer list for a submitted QuestionnaireResponse. Question text is
 * looked up in the source Questionnaire by linkId, falling back to the QR item's own text
 * or linkId when a question isn't found in the Questionnaire.
 */
export const QuestionnaireResponseAnswers = ({
  questionnaireResponse,
  questionnaire,
}: QuestionnaireResponseAnswersProps): React.ReactElement => {
  const { t } = useTranslation('records');

  const questionTextByLinkId = useMemo(() => {
    const byLinkId = new Map<string, string>();
    collectItemText(questionnaire.item, byLinkId);
    return byLinkId;
  }, [questionnaire]);

  const answeredItems = (questionnaireResponse.item ?? []).filter((item) => (item.answer ?? []).length > 0);

  if (answeredItems.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('noAnswer')}</p>;
  }

  return (
    <dl className="space-y-2" data-testid="questionnaire-response-answers">
      {answeredItems.map((item, idx) => {
        const label =
          (item.linkId ? questionTextByLinkId.get(item.linkId) : undefined) ??
          item.text ??
          item.linkId ??
          t('questionFallback', { number: idx + 1 });
        const formatted = (item.answer ?? [])
          .map((answer) => formatQuestionnaireAnswer(answer))
          .filter((s): s is string => s !== null);
        return (
          <div key={item.linkId ?? idx} className="flex flex-col gap-0.5">
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className="text-sm">{formatted.length > 0 ? formatted.join(', ') : t('noAnswer')}</dd>
          </div>
        );
      })}
    </dl>
  );
};
